import { supabase } from './supabase-client.js';

const API_BASE = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:8787'
  : 'https://pwd-api.adam-hoemberg.workers.dev';

const CUTOVER_API_BASE = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? API_BASE
  : 'https://cxjbuevhijweijyoowvg.supabase.co/functions/v1/driver-auth-cutover';

const MANAGER_AUTH_DRIFT_SIGNAL = 'Manager authentication required';

function getSessionToken(): string | null {
  return localStorage.getItem('pwd_manager_session');
}

function resolveBase(path: string): string {
  if (path.startsWith('/api/directory')) return CUTOVER_API_BASE;
  if (path.startsWith('/api/safety-meeting/history')) return CUTOVER_API_BASE;
  if (path === '/api/fleet/vehicles' || path.startsWith('/api/fleet/vehicles?')) return CUTOVER_API_BASE;
  if (path === '/api/scheduler/roster' || path.startsWith('/api/scheduler/roster?')) return CUTOVER_API_BASE;
  if (path === '/api/scheduler/grid' || path.startsWith('/api/scheduler/grid?')) return CUTOVER_API_BASE;
  if (path === '/api/scheduler/time-off' || path.startsWith('/api/scheduler/time-off?')) return CUTOVER_API_BASE;
  if (path === '/api/scorecard/thresholds' || path.startsWith('/api/scorecard/thresholds?')) return CUTOVER_API_BASE;
  if (path === '/api/manager-activity' || path.startsWith('/api/manager-activity?')) return CUTOVER_API_BASE;
  if (path === '/api/activity/clock-overview' || path.startsWith('/api/activity/clock-overview?')) return CUTOVER_API_BASE;
  if (path === '/api/admin/email-health' || path.startsWith('/api/admin/email-health?')) return CUTOVER_API_BASE;
  return API_BASE;
}

/**
 * Stale-token self-heal.
 *
 * Single-session enforcement writes a new `employees.manager_session_token`
 * on every OAuth. A browser still holding an earlier token will hit 401
 * "Manager authentication required" on every worker call. We centralize the
 * recovery here so no module has to reinvent it.
 *
 * - Clears localStorage `pwd_manager_session` so the shell gate drops.
 * - Local Supabase sign-out (no network) so `onAuthStateChange('SIGNED_OUT')`
 *   fires and ManagerShell flips back to ManagerOAuth for a fresh Google flow.
 *
 * Safe to call multiple times; all steps are idempotent.
 */
async function resetManagerAuthDrift(): Promise<void> {
  try {
    window.localStorage.removeItem('pwd_manager_session');
    // StorageEvent only fires in OTHER tabs.  Dispatch a synthetic one so
    // ManagerShell's subscribeManagerSession listener picks it up in THIS tab.
    window.dispatchEvent(
      new StorageEvent('storage', { key: 'pwd_manager_session', newValue: null }),
    );
  } catch {
    /* storage unavailable — nothing to clear */
  }
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    /* already signed out or offline — ignore */
  }
}

/**
 * Detect the specific 401 that indicates our manager session token no longer
 * matches the DB. Other 401s (permission-gated endpoints, etc.) pass through
 * untouched so callers can still render their own error UI.
 */
async function maybeHandleAuthDrift(response: Response): Promise<void> {
  if (response.status !== 401) return;
  let errorMessage = '';
  try {
    const body = (await response.clone().json()) as { error?: unknown };
    if (typeof body?.error === 'string') errorMessage = body.error;
  } catch {
    /* body not JSON — cannot confirm drift; bail out */
    return;
  }
  if (errorMessage.includes(MANAGER_AUTH_DRIFT_SIGNAL)) {
    await resetManagerAuthDrift();
  }
}

export async function apiFetch<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getSessionToken();
  const response = await fetch(resolveBase(path) + path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  await maybeHandleAuthDrift(response);

  return response.json() as Promise<T>;
}

export async function apiWrite<T = unknown>(
  path: string,
  method: string,
  body: unknown,
): Promise<{ ok: boolean; data: T }> {
  const token = getSessionToken();
  const response = await fetch(API_BASE + path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  await maybeHandleAuthDrift(response);

  const data = (await response.json()) as T;
  return { ok: response.ok, data };
}
